import { Input, InputFooter, InputHeader } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Select } from "@amzn/stencil-react-components/select";
import { Label } from "@amzn/stencil-react-components/text";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useLocations } from "hooks/useLocations";
import { UserPermissions } from "lib/services";
import React, { FC } from "react";
import { useChangeAddressFormActions, useChangeAddressFormItems } from "view/Page/CheckoutPage/useChangeAddressForm";

const stringPath = "checkout.formPanel.shippingDetailsCard";

export interface ChangeShippingDetailsProps {
  addressItems: useChangeAddressFormItems;
  addressActions: useChangeAddressFormActions;
}

const ChangeShippingDetails: FC<ChangeShippingDetailsProps> = (props: ChangeShippingDetailsProps) => {
  const {
    countries,
    phoneCodes,
    states,
    cities,
    isStateDisabled,
    isCityDisabled,
    useTextInputStates,
    useTextInputCities,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
  } = useLocations(props.addressItems.formValues.country, props.addressActions.handleInputChange);

  const renderCountry = () => {
    return (
      <>
        <InputHeader labelText={getString(`${stringPath}.country`)} htmlFor="select-country" required />
        <Select
          isSearchable={true}
          width="73%"
          id="select-country"
          data-testid="select-country"
          isMulti={false}
          labelId="some-select-label"
          options={countries.map((c) => c.name)}
          value={props.addressItems.formValues.country}
          onChange={(selectedValue) => handleCountryChange(selectedValue)}
        />
      </>
    );
  };

  const renderStateProvinceRegion = () => {
    if (useTextInputStates) {
      return (
        <>
          <InputHeader labelText={getString(`${stringPath}.stateProvinceRegion`)} htmlFor="input-state" required />
          <Input
            dataTestId="input-state"
            id="input-state"
            value={props.addressItems.formValues.citySubdivision1}
            onChange={(event) => props.addressActions.handleInputChange("citySubdivision1", event.target.value)}
          />
        </>
      );
    }

    return (
      <>
        <InputHeader labelText={getString(`${stringPath}.stateProvinceRegion`)} htmlFor="select-state" required />
        <Select
          noOptionsText="Nothing found"
          isSearchable={true}
          id="select-state"
          data-testid="select-state"
          isMulti={false}
          labelId="select-state-label"
          options={states ?? []}
          value={props.addressItems.formValues.citySubdivision1}
          onChange={handleStateChange}
          disabled={isStateDisabled}
        />
      </>
    );
  };

  const renderCity = () => {
    if (useTextInputCities) {
      return (
        <>
          <InputHeader labelText={getString(`${stringPath}.city`)} htmlFor="input-city" required />
          <Input
            dataTestId="input-city"
            id="input-city"
            value={props.addressItems.formValues.city}
            onChange={(event) => props.addressActions.handleInputChange("city", event.target.value)}
            required
            disabled={isCityDisabled}
          />
        </>
      );
    }

    return (
      <>
        <InputHeader labelText={getString(`${stringPath}.city`)} htmlFor="input-city" required />
        <Select
          noOptionsText="Nothing found"
          isSearchable={true}
          id="select-city"
          data-testid="select-city"
          isMulti={false}
          labelId="select-city-label"
          options={cities ?? []}
          value={props.addressItems.formValues.city}
          onChange={handleCityChange}
          disabled={isCityDisabled}
        />
      </>
    );
  };

  return (
    <>
      <Col gridGap={"S100"}>
        <InputHeader labelText={getString(`${stringPath}.alias`)} htmlFor="input-alias" required />
        <Input id="input-alias" value={UserPermissions.current.username ?? ""} disabled />
      </Col>
      <Col gridGap={"S100"}>
        <InputHeader labelText={getString(`${stringPath}.fullName`)} htmlFor="input-fullname" required />
        <Input
          id="input-fullname"
          value={props.addressItems.formValues.fullName}
          onChange={(event) => props.addressActions.handleInputChange("fullName", event.target.value)}
        />
      </Col>
      <Col gridGap={"S100"}>{renderCountry()}</Col>
      <Col gridGap={"S100"}>
        <Row gridGap={"S100"} alignItems="end">
          <InputHeader labelText={getString(`${stringPath}.addressLine1`)} htmlFor="input-line1" required />
          <Label color="neutral70" fontSize="T100">
            {getString(`${stringPath}.line1description`)}
          </Label>
        </Row>
        <Input
          id="input-line1"
          value={props.addressItems.formValues.addressLine1}
          onChange={(event) => props.addressActions.handleInputChange("addressLine1", event.target.value)}
          required
        />
      </Col>
      <Col gridGap={"S100"}>
        <Row gridGap={"S100"} alignItems="end">
          <InputHeader labelText={getString(`${stringPath}.addressLine2`)} htmlFor="input-line2" />
          <Label color="neutral70" fontSize="T100">
            {getString(`${stringPath}.line2description`)}
          </Label>
        </Row>
        <Input
          id="input-line2"
          value={props.addressItems.formValues.addressLine2}
          onChange={(event) => props.addressActions.handleInputChange("addressLine2", event.target.value)}
        />
      </Col>
      <Row gridGap={"S300"}>
        <Col gridGap={"S100"} width={"50%"}>
          {renderStateProvinceRegion()}
        </Col>
        <Col gridGap={"S100"} width="50%">
          {renderCity()}
        </Col>
      </Row>
      <Col gridGap={"S100"} width={301}>
        <InputHeader labelText={getString(`${stringPath}.zipCode`)} htmlFor="input-zipcode" required />
        <Input
          id="input-zipcode"
          value={props.addressItems.formValues.postalCode}
          onChange={(event) => props.addressActions.handleInputChange("postalCode", event.target.value)}
        />
      </Col>
      <Row gridGap={"S300"}>
        <Col gridGap={"S100"} width={150}>
          <InputHeader labelText={getString(`${stringPath}.countryCode`)} htmlFor="select-countrycode" required />
          <Select
            isSearchable={true}
            id="select-countrycode"
            data-testid="select-countrycode"
            isMulti={false}
            labelId="select-countrycode"
            placeholder="code"
            options={phoneCodes}
            value={`+ ${props.addressItems.formValues.countryCode}`}
            onChange={(selectedValue) => props.addressActions.handleInputChange("countryCode", selectedValue)}
          />
        </Col>
        <Col gridGap={"S100"} width="100%">
          <InputHeader
            error={props.addressItems.isInvalidPhoneNumber}
            labelText={getString(`${stringPath}.phoneNumber`)}
            htmlFor="input-phonenumber"
            required
          />
          <Input
            id="input-phonenumber"
            data-testid="input-phone-number"
            value={props.addressItems.formValues.phoneNumber}
            aria-invalid={props.addressItems.isInvalidPhoneNumber}
            aria-describedby="invalid-phone-number-footer"
            error={props.addressItems.isInvalidPhoneNumber}
            onChange={(event) => props.addressActions.handleInputChange("phoneNumber", event.target.value)}
          />
          {props.addressItems.isInvalidPhoneNumber && (
            <InputFooter id="invalid-phone-number-footer" error>
              {getString(`${stringPath}.invalidPhoneNumber`)}
            </InputFooter>
          )}
        </Col>
      </Row>
    </>
  );
};

export default ChangeShippingDetails;
